"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageShell from "../components/layout/PageShell";
import Section from "../components/layout/Section";
import Container from "../components/layout/Container";
import SiteFooter from "../components/layout/SiteFooter";
import VelvetRopeGate from "./VelvetRopeGate";
import WhatsAppInvite from "./WhatsAppInvite";

// --- Data Definition ---
interface PrivateMoment {
    id: number;
    title: string;
    text: string;
    image: string;
    layout: "left" | "right";
}

const privateMoments: PrivateMoment[] = [
    {
        id: 1,
        title: "08:00 — The Morning Ritual",
        text: "Before the emails, the strategy, and the persona. Just coffee, the cold Munich air, and the silence of a city waking up.",
        image: "/images/private/moments/private_01_morning_coffee_walk.jpeg",
        layout: "left"
    },
    {
        id: 2,
        title: "09:30 — Body & Discipline",
        text: "It’s not about looking good; it’s about feeling capable. The iron doesn't lie. A private glimpse into the work that builds the silhouette.",
        image: "/images/private/moments/private_02_gym_mirror.jpeg",
        layout: "right"
    },
    {
        id: 3,
        title: "13:00 — Slow Sustenance",
        text: "Making pasta by hand. It’s inefficient, messy, and meditative. The antidote to a digital life is analog pleasure.",
        image: "/images/private/moments/private_03_kitchen_hands.jpeg",
        layout: "left"
    },
    {
        id: 4,
        title: "16:00 — Transit & Texture",
        text: "Watching the rain streak against the glass on the way to a meeting. The world blurs into impressionism. Safe inside the leather cocoon.",
        image: "/images/private/moments/private_04_city_from_behind.jpeg",
        layout: "right"
    },
    {
        id: 5,
        title: "20:00 — Unfiltered Conversations",
        text: "Shoes off, legs on the sofa, wine poured. The part of the evening where the 'brand' dissolves and the human emerges.",
        image: "/images/private/moments/private_05_late_night_sofa.jpeg",
        layout: "left"
    },
    {
        id: 6,
        title: "21:30 — The Sanctuary",
        text: "Bathing is a ritual of separation. Washing away the city, the noise, and the expectations. Water, steam, and silence.",
        image: "/images/private/moments/private_06_rainy_window.jpeg",
        layout: "right"
    },
    {
        id: 7,
        title: "23:00 — Intimate Circles",
        text: "A dinner table that's messy with laughter and half-empty glasses. No networking, just connection. The people who knew me before I was 'Hikari'.",
        image: "/images/private/moments/private_07_friends_table.jpeg",
        layout: "left"
    },
    {
        id: 8,
        title: "07:00 — A New Light",
        text: "Waking up with the sun. The sheets are rumpled, the light is gold. A quiet moment of gratitude before the performance begins again.",
        image: "/images/private/moments/private_08_close_selfie.jpeg",
        layout: "right"
    }
];

export default function PrivatePage() {
    const [isGatePassed, setIsGatePassed] = useState(false);

    // Optional: Check local storage logic handled by parent or gate? 
    // For now we keep state local as requested for testing ("don't persist" was implied or "disable persistence").

    return (
        <PageShell>
            {/* 1. Hero Header */}
            <div className="relative w-full h-[85vh] overflow-hidden">
                <div className="absolute inset-0 bg-neutral-900">
                    <img
                        src="/images/private/private_hero_A.png"
                        alt="Hikari Private"
                        className="w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#EAE8E4]" />
                </div>

                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 text-white mix-blend-overlay">
                    <span className="font-sans text-xs tracking-[0.3em] uppercase opacity-80 mb-4">
                        Members Only
                    </span>
                    <h1 className="font-display text-6xl md:text-8xl tracking-tight leading-none mb-6">
                        The Inner Circle
                    </h1>
                </div>
            </div>

            {/* 2. Editorial Section Header */}
            <Section className="!pb-0">
                <div className="text-center max-w-2xl mx-auto space-y-6">
                    <h2 className="font-display text-4xl md:text-5xl text-neutral-800">
                        What waits inside?
                    </h2>
                    <p className="font-serif italic text-xl text-neutral-500">
                        Beyond the curated feed, life is messy, warm, and real.
                    </p>
                    <div className="w-px h-12 bg-neutral-300 mx-auto" />
                </div>
            </Section>

            {/* 3. Narrative Moment Blocks */}
            <Section>
                <div className="space-y-32">
                    {privateMoments.map((moment) => (
                        <div key={moment.id} className={`flex flex-col md:flex-row gap-12 md:gap-24 items-center ${moment.layout === "right" ? "md:flex-row-reverse" : ""}`}>
                            {/* Image Side */}
                            <div className="w-full md:w-1/2 aspect-[3/4] relative group cursor-pointer overflow-hidden rounded-sm bg-neutral-200">
                                <img
                                    src={moment.image}
                                    alt={moment.title}
                                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" // Slow parallax-like feel
                                />
                            </div>

                            {/* Text Side */}
                            <div className="w-full md:w-1/2 space-y-6 text-center md:text-left">
                                <span className="text-[10px] tracking-widest uppercase text-neutral-400">
                                    Moment {String(moment.id).padStart(2, '0')}
                                </span>
                                <h3 className="font-display text-3xl md:text-4xl text-neutral-800">
                                    {moment.title}
                                </h3>
                                <p className="font-sans text-neutral-600 leading-relaxed max-w-sm mx-auto md:mx-0">
                                    {moment.text}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* 4. Lead-in Text + Gate + WhatsApp */}
            <Section className="bg-white/50 border-t border-neutral-200">
                <div className="max-w-xl mx-auto text-center space-y-12">

                    {/* Lead-in */}
                    <div className="space-y-6">
                        <h2 className="font-display text-3xl text-neutral-800">
                            The Velvet Rope
                        </h2>
                        <p className="font-sans text-neutral-600 leading-relaxed">
                            This is not for fans. This is for friends. I keep this circle small intentionally. Access requires a conscious step—a confirmation that you are here for the right reasons.
                        </p>
                    </div>

                    {/* Gate / Invite Switch */}
                    <div className="relative min-h-[200px] flex flex-col items-center justify-center">
                        <AnimatePresence mode="wait">
                            {!isGatePassed ? (
                                <motion.div
                                    key="gate"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, filter: "blur(10px)" }}
                                    transition={{ duration: 0.5 }}
                                    className="w-full"
                                >
                                    <VelvetRopeGate onPass={() => setIsGatePassed(true)} />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="invite"
                                    initial={{ opacity: 0, scale: 0.95, filter: "blur(10px)" }}
                                    animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                    className="w-full"
                                >
                                    <WhatsAppInvite />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                </div>
            </Section>

            {/* 5. Footer */}
            <SiteFooter />

        </PageShell>
    );
}
